// src/api/sessionPersistenceApi.ts
//
// Reads, writes and clears the stored conversation for one agent. The backend
// holds the payload opaquely - its shape is owned by sessionSnapshot.ts - and
// keys it on the Azure oid from the bearer token, so there is nothing to pass
// but the agent id.
//
// None of these functions throw. Persistence is best-effort and sits on the
// boot path and on a background debounce, so a failure has to degrade to
// "start empty" or "try again on the next tick" rather than reach a render.
// Every outcome is encoded in the return value (the userMeApi convention).

import { PersistedSessionPayloadV1 } from "../interfaces/chatSession";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { handleAuthRejection } from "./utils/errorHandler";

const log = createLogger("SessionPersistenceApi");

const API_BASE_URL = process.env.API_BASE_URL;

// Bound every call so a hung backend can neither hold up the boot paint nor
// pile up saves behind one another. AbortController + setTimeout rather than
// AbortSignal.timeout() because browserslist still targets IE 11 (matches
// userMeApi.ts / draftingAssistantApi.ts).
const FETCH_TIMEOUT_MS = 10_000;

/**
 * Tighter budget for the launch-time read, which the pane waits behind before
 * it paints anything. A slow read costs the user a blank pane, and there is a
 * good fallback - start empty - so it gives up sooner than a write would.
 * Saving stays off for any agent whose read did not conclude, so giving up
 * early cannot cost the stored conversation.
 */
const READ_TIMEOUT_MS = 4_000;

/**
 * Client-error statuses that describe the moment rather than the payload, so
 * the same body is worth sending again. 401 in particular is expected on a
 * long-lived pane: reading it as a permanent verdict would drop the write
 * precisely when the refresh it triggers is about to make one succeed.
 */
const RETRYABLE_CLIENT_ERRORS = new Set([401, 408, 429]);

/**
 * The stored session as the backend returns it. `payload` is deliberately
 * untyped here: the backend echoes back whatever was written, including a
 * payload from an older build, so it is only trusted after
 * `rehydrateSessionPayload` has checked its schema version.
 */
export interface SessionEnvelope {
  agent_id: string;
  /**
   * Whether the user actually has a session stored for this agent. Reading a
   * session that was never stored is a successful call answering "none", not an
   * error, so this is the flag to branch on - the remaining fields are null
   * whenever it is false.
   */
  stored: boolean;
  /** The per-agent backend chat id, threaded back so usage stays one session. */
  backend_session_id: string | null;
  schema_version: number | null;
  payload: unknown;
  updated_at: string | null;
}

/** The body of a save. A save is a full replace, so every field is sent. */
export interface SessionStateUpdate {
  backend_session_id: string | null;
  schema_version: number;
  payload: PersistedSessionPayloadV1;
}

/**
 * Outcome of a save or a delete.
 *
 * The `rejected` / `failed` split is what a retry should branch on: `rejected`
 * is a verdict about this payload (too large, unknown agent, no access) and
 * resending it unchanged produces the same answer, whereas `failed` is a
 * transient condition worth trying again on the next debounce tick.
 */
export type SessionWriteResult =
  | { kind: "saved" }
  | { kind: "rejected"; httpStatus: number }
  | { kind: "failed"; httpStatus?: number };

/**
 * Outcome of a read.
 *
 * "nothing stored" and "could not find out" must stay distinguishable: a save
 * is a full replace, so writing after a failed read would overwrite a stored
 * conversation with whatever the empty pane has accumulated. Only a conclusive
 * answer - `stored` or `empty` - makes it safe to start saving that agent.
 */
export type SessionReadResult =
  | { kind: "stored"; envelope: SessionEnvelope }
  | { kind: "empty" }
  | { kind: "failed" };

function sessionUrl(agentId: string): string {
  // The trailing slash is mandatory: without it the backend answers 307 and
  // the redirect drops the PUT body.
  return `${API_BASE_URL}/sessions/${encodeURIComponent(agentId)}/`;
}

/**
 * Run one call under a timeout, returning null when it could not be completed
 * at all (network failure, timeout) rather than letting it throw.
 *
 * @param send Issues the call, bounded by the abort signal it is handed
 * @param context Identifies the call in the log when it does not complete
 */
async function fetchWithTimeout(
  send: (signal: AbortSignal) => Promise<Response>,
  context: { agentId: string; method: string },
  timeoutMs: number = FETCH_TIMEOUT_MS
): Promise<Response | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await send(controller.signal);
  } catch (error) {
    // An abort (timeout) and a refused connection mean the same thing to the
    // caller - no verdict from the backend.
    log.warn("Session persistence request did not complete", {
      ...context,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch the stored session for one agent.
 *
 * @param agentId The agent whose session to read
 * @param accessToken Bearer token for the current user
 * @returns Whether a session was stored, none was, or the read did not conclude
 */
export async function fetchSessionState(
  agentId: string,
  accessToken: string | null
): Promise<SessionReadResult> {
  if (!API_BASE_URL) {
    log.error("API_BASE_URL not configured");
    return { kind: "failed" };
  }

  const url = sessionUrl(agentId);
  const response = await fetchWithTimeout(
    (signal) => fetch(constructRequest(url, "GET", accessToken, undefined, signal)),
    { agentId, method: "GET" },
    READ_TIMEOUT_MS
  );

  if (!response) {
    return { kind: "failed" };
  }

  handleAuthRejection(response);

  if (!response.ok) {
    log.warn("Failed to read stored session", { agentId, status: response.status });
    return { kind: "failed" };
  }

  try {
    const envelope = (await response.json()) as SessionEnvelope;

    if (!envelope?.stored) {
      log.debug("No stored session for agent", { agentId });
      return { kind: "empty" };
    }

    log.info("Read stored session", {
      agentId,
      schemaVersion: envelope.schema_version,
      hasBackendSessionId: !!envelope.backend_session_id,
      updatedAt: envelope.updated_at,
    });
    return { kind: "stored", envelope };
  } catch (error) {
    log.warn("Stored session body was not valid JSON", {
      agentId,
      error: error instanceof Error ? error.message : String(error),
    });
    return { kind: "failed" };
  }
}

/**
 * Store the session for one agent, replacing whatever was there.
 *
 * @param agentId The agent whose session to write
 * @param update The full envelope to store
 * @param accessToken Bearer token for the current user
 * @returns Whether the write landed, and if not whether retrying could help
 */
export async function saveSessionState(
  agentId: string,
  update: SessionStateUpdate,
  accessToken: string | null
): Promise<SessionWriteResult> {
  if (!API_BASE_URL) {
    log.error("API_BASE_URL not configured");
    return { kind: "rejected", httpStatus: 0 };
  }

  const url = sessionUrl(agentId);

  let body: string;
  try {
    // Postgres rejects \u0000 inside a jsonb value ("cannot be converted to
    // text"), which would 500 the write and then fail identically on every
    // retry, freezing this agent's stored session for good. A NUL carries no
    // meaning in a transcript, so it is dropped rather than allowed to poison
    // the payload.
    body = JSON.stringify(update).replace(/\\u0000/g, "");
  } catch (error) {
    // Nothing in a pruned payload should be unserializable, but this module
    // promises never to throw and it is called from a bare `void` in a timer.
    log.error("Session payload could not be serialized", {
      agentId,
      error: error instanceof Error ? error.message : String(error),
    });
    return { kind: "rejected", httpStatus: 0 };
  }

  log.debug("Saving session", {
    agentId,
    messageCount: update.payload.messages.length,
    serializedLength: body.length,
  });

  const response = await fetchWithTimeout(
    (signal) => {
      // constructRequest only carries a FormData body and sets no Content-Type,
      // so take its headers (auth plus the WAF token) and attach the JSON body
      // here - the same override outlookMailApi uses for its JSON POSTs.
      const authenticated = constructRequest(url, "PUT", accessToken);
      const headers = new Headers(authenticated.headers);
      headers.set("Content-Type", "application/json");
      return fetch(authenticated.url, { method: "PUT", headers, body, signal });
    },
    { agentId, method: "PUT" }
  );

  if (!response) {
    return { kind: "failed" };
  }

  handleAuthRejection(response);

  if (response.ok) {
    log.info("Saved session", { agentId, messageCount: update.payload.messages.length });
    return { kind: "saved" };
  }

  // Most 4xx answers are a verdict on this payload: an oversized transcript
  // stays oversized, so resending it unchanged only burns requests. The
  // exceptions are the ones that describe the moment rather than the payload -
  // an expired token (whose refresh handleAuthRejection has just started), a
  // timeout, or a rate limit - and those are worth another attempt.
  if (
    response.status >= 400 &&
    response.status < 500 &&
    !RETRYABLE_CLIENT_ERRORS.has(response.status)
  ) {
    log.warn("Session save rejected", { agentId, status: response.status });
    return { kind: "rejected", httpStatus: response.status };
  }

  log.warn("Session save failed", { agentId, status: response.status });
  return { kind: "failed", httpStatus: response.status };
}

/**
 * Delete the stored session for one agent. Idempotent - deleting a session
 * that was never stored succeeds.
 *
 * @param agentId The agent whose session to clear
 * @param accessToken Bearer token for the current user
 * @returns Whether the delete landed, and if not whether retrying could help
 */
export async function deleteSessionState(
  agentId: string,
  accessToken: string | null
): Promise<SessionWriteResult> {
  if (!API_BASE_URL) {
    log.error("API_BASE_URL not configured");
    return { kind: "rejected", httpStatus: 0 };
  }

  const url = sessionUrl(agentId);
  const response = await fetchWithTimeout(
    (signal) => fetch(constructRequest(url, "DELETE", accessToken, undefined, signal)),
    { agentId, method: "DELETE" }
  );

  if (!response) {
    return { kind: "failed" };
  }

  handleAuthRejection(response);

  if (response.ok) {
    log.info("Deleted stored session", { agentId });
    return { kind: "saved" };
  }

  if (response.status >= 400 && response.status < 500) {
    log.warn("Session delete rejected", { agentId, status: response.status });
    return { kind: "rejected", httpStatus: response.status };
  }

  log.warn("Session delete failed", { agentId, status: response.status });
  return { kind: "failed", httpStatus: response.status };
}
